import { useEffect, useMemo, useState } from "react";
import { BookUser, Plus, Search, Pencil, Trash2, Tags, Send, Loader2 } from "lucide-react";
import { IconTel, IconEmail, IconPec, IconAddr, IconNote } from "../../components/RubricaIcons";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import WhatsAppLink from "../../components/WhatsAppLink";
import EmailDialog from "../../components/EmailDialog";
import PecDialog from "../../components/PecDialog";
import RubricaContattoDialog from "../../components/RubricaContattoDialog";
import RubricaCategorieDialog from "../../components/RubricaCategorieDialog";

const all = (c, k) => [c[k], ...(c[`${k}_extra`] || [])].filter(Boolean);
const nomeContatto = (c) => [c.cognome, c.nome].filter(Boolean).join(" ") || c.azienda || "—";

function Row({ icon, children, testId }) {
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testId}>
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1 space-y-0.5">{children}</div>
    </div>
  );
}

function ContattoCard({ c, cat, onEdit, onDelete, onMail, onPec }) {
  const tel = all(c, "telefono"); const em = all(c, "email"); const pec = all(c, "pec");
  const addr = [[c.indirizzo, c.numero_civico].filter(Boolean).join(" "), [c.cap, c.comune, c.provincia && `(${c.provincia})`].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <div className="group rounded-xl border border-slate-800 bg-slate-900/40 p-4 transition-shadow hover:shadow-lg flex flex-col gap-3" data-testid={`rub-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-100 truncate uppercase" data-testid={`rub-card-name-${c.id}`}>{nomeContatto(c)}</div>
          {(c.cognome || c.nome) && <div className="text-[11px] text-slate-500 truncate">{[c.cognome && `Cognome: ${c.cognome}`, c.nome && `Nome: ${c.nome}`].filter(Boolean).join(" · ")}</div>}
          {c.azienda && (c.cognome || c.nome) && <div className="text-xs text-slate-400 truncate">{c.azienda}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {cat && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: `${cat.colore}22`, color: cat.colore }} data-testid={`rub-card-cat-${c.id}`}>{cat.nome}</span>}
          <button type="button" onClick={onEdit} title="Modifica" data-testid={`rub-edit-${c.id}`} className="p-1 text-slate-400 hover:text-amber-500"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={onDelete} title="Elimina" data-testid={`rub-del-${c.id}`} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {tel.length > 0 && <Row icon={<IconTel />} testId={`rub-card-tel-${c.id}`}>{tel.map((t, i) => (
        <div key={i} className="flex items-center gap-1.5"><a href={`tel:${t}`} className="font-mono text-slate-200 hover:underline">{t}</a><WhatsAppLink number={t} size="h-4 w-4" testId={`rub-wa-${c.id}-${i}`} /></div>
      ))}</Row>}
      {em.length > 0 && <Row icon={<IconEmail />} testId={`rub-card-email-${c.id}`}>{em.map((e, i) => (
        <div key={i} className="flex items-center gap-1.5"><span className="truncate text-slate-200">{e}</span>
          <button type="button" onClick={() => onMail(e)} title="Invia email" data-testid={`rub-mail-${c.id}-${i}`} className="text-orange-500 hover:text-orange-400"><Send className="h-3.5 w-3.5" /></button></div>
      ))}</Row>}
      {pec.length > 0 && <Row icon={<IconPec />} testId={`rub-card-pec-${c.id}`}>{pec.map((e, i) => (
        <div key={i} className="flex items-center gap-1.5"><span className="truncate text-slate-200">{e}</span>
          <button type="button" onClick={() => onPec(e)} title="Invia PEC" data-testid={`rub-pec-${c.id}-${i}`} className="text-blue-600 hover:text-blue-500"><Send className="h-3.5 w-3.5" /></button></div>
      ))}</Row>}
      {addr && <Row icon={<IconAddr />} testId={`rub-card-addr-${c.id}`}><span className="text-slate-300">{addr}</span></Row>}
      {c.note && <Row icon={<IconNote />} testId={`rub-card-note-${c.id}`}><span className="text-slate-400 whitespace-pre-line line-clamp-3">{c.note}</span></Row>}
    </div>
  );
}

export default function Rubrica() {
  const [contatti, setContatti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [edit, setEdit] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [mailTo, setMailTo] = useState(null);
  const [pecTo, setPecTo] = useState(null);
  const loadCats = () => api.get("/studio/rubrica-studio/categorie").then(({ data }) => setCategorie(data || [])).catch(() => {});
  const load = () => api.get("/studio/rubrica-studio/contatti").then(({ data }) => setContatti(data || []))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  useEffect(() => { loadCats(); load(); }, []);
  const catMap = useMemo(() => Object.fromEntries(categorie.map((c) => [c.id, c])), [categorie]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase(); const digits = s.replace(/\D/g, "");
    return contatti.filter((c) => {
      if (fCat && (fCat === "__none" ? c.categoria_id && catMap[c.categoria_id] : c.categoria_id !== fCat)) return false;
      if (!s) return true;
      const hay = [c.nome, c.cognome, c.azienda, ...all(c, "email"), ...all(c, "pec"), c.note, catMap[c.categoria_id]?.nome].join(" ").toLowerCase();
      return hay.includes(s) || (digits.length >= 3 && all(c, "telefono").some((t) => t.replace(/\D/g, "").includes(digits)));
    });
  }, [contatti, q, fCat, catMap]);
  const del = async (c) => {
    if (!window.confirm(`Eliminare il contatto «${nomeContatto(c)}»?`)) return;
    try { await api.delete(`/studio/rubrica-studio/contatti/${c.id}`); setContatti((l) => l.filter((x) => x.id !== c.id)); toast.success("Contatto eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const chip = (id, label, color) => (
    <button key={id || "all"} type="button" onClick={() => setFCat(id)} data-testid={`rub-filter-${id || "all"}`}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${fCat === id ? "border-amber-500 bg-amber-500/15 text-amber-500" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
      {color && <span className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ background: color }} />}{label}
    </button>
  );
  return (
    <div data-testid="rubrica-page">
      <PageHeader title="Rubrica" subtitle="I contatti dello studio, separati dal resto del gestionale." icon={BookUser}
        actions={<div className="flex gap-2">
          <button type="button" onClick={() => setCatOpen(true)} data-testid="rub-categorie-btn" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-700 text-sm text-slate-300 hover:bg-slate-800/60"><Tags className="h-4 w-4" />Categorie</button>
          <button type="button" onClick={() => setEdit({})} data-testid="rub-new-btn" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-[#0a0d14] text-sm font-semibold"><Plus className="h-4 w-4" />Nuovo contatto</button>
        </div>} />
      <div className="mb-5 space-y-3">
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, azienda, numero, email…" data-testid="rub-search"
            className="h-10 w-full rounded-md border border-slate-700 bg-slate-900/60 pl-9 pr-3 text-sm text-slate-100" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chip("", `Tutti (${contatti.length})`)}
          {categorie.map((c) => chip(c.id, c.nome, c.colore))}
          {chip("__none", "Senza categoria")}
        </div>
      </div>
      {loading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
        : !filtered.length ? <div className="py-16 text-center text-sm text-slate-500" data-testid="rub-empty">{contatti.length ? "Nessun contatto corrisponde alla ricerca." : "La rubrica è vuota. Aggiungi il primo contatto."}</div>
        : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="rub-grid">
          {filtered.map((c) => <ContattoCard key={c.id} c={c} cat={catMap[c.categoria_id]} onEdit={() => setEdit(c)} onDelete={() => del(c)} onMail={setMailTo} onPec={setPecTo} />)}
        </div>}
      <RubricaContattoDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} contatto={edit} categorie={categorie} onMail={setMailTo} onPec={setPecTo}
        onSaved={(d) => { setEdit(null); setContatti((l) => (l.some((x) => x.id === d.id) ? l.map((x) => (x.id === d.id ? d : x)) : [...l, d])); }} />
      <RubricaCategorieDialog open={catOpen} onOpenChange={setCatOpen} categorie={categorie} onChanged={() => { loadCats(); load(); }} />
      <EmailDialog open={!!mailTo} onOpenChange={(o) => !o && setMailTo(null)} pratica={null} prefillTo={mailTo || ""} />
      <PecDialog open={!!pecTo} onOpenChange={(o) => !o && setPecTo(null)} pratica={null} prefillTo={pecTo || ""} />
    </div>
  );
}
